import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { GetOrdersByIdApi, GetOrdersVendorByIdApi, MyAccounts, UpdateOrderStatus } from "../../common/CommonApi";
import Swal from "sweetalert2";

const OrderStatusPage = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(true);
  const [account, setAccount] = useState(null);

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [orderDetails, setOrderDetails] = useState(null);

  const getDisplayOrderId = (order) => order?.sourceOrderId || order?._id;

  useEffect(() => {
    FetchData();
  }, []);

  const FetchData = async () => {
    const currentAccount = account || await MyAccounts();
    setAccount(currentAccount);
    setIsAdmin(!currentAccount?.isVendor);

    const resp = currentAccount?.isVendor
      ? await GetOrdersVendorByIdApi(id)
      : await GetOrdersByIdApi(id);

    if (resp.data.message === "DATA_FOUND") {
      const order = resp.data.data[0]
      // console.log("--->order111",order)
       const formattedData = {
        _id: order._id,
        displayOrderId: getDisplayOrderId(order),
        totalAmount: order.finalAmount,
        status: order.status,
        address: {
          name: order.addressInfo?.receiversName,
          street: order.addressInfo?.addressStreetArea,
          city: order.addressInfo?.cityDistict,
          state: order.addressInfo?.state,
          zip: order.addressInfo?.pincode,
          phone: order.addressInfo?.receiversPhone,
        },
        createdAt: new Date(order.createdDate),

        // ✅ use backend timeline
        timeline: (order.timeline || []).sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        ),
      };
      setNewStatus(order.status);
      console.log("--->formattedData",formattedData)
      setOrder(formattedData);
      setOrderDetails(resp.data.data[0]); // since backend returns [ {...} ]
    } else {
      Swal.fire("Error", "Order not found", "error");
    }
  };



  // New admin form fields
  const [newStatus, setNewStatus] = useState(order?.status || "pending");
  const [description, setDescription] = useState("");
  
  const statusSteps = ["pending", "processing", "confirmed", "shipped", "delivered", "cancelled"];

  // Simulate API fetch
  useEffect(() => {
    async function fetchOrder() {
      // Replace with your API call
      setLoading(false);
      // setNewStatus(fakeData.status);
    }
    fetchOrder();
  }, []);

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (!newStatus || !description.trim()) {
    alert("Please select status and enter a description.");
    return;
  }

  // 🔥 STEP 1: Optimistic UI update (instant change in UI)
  setOrder((prev) => ({
    ...prev,
    status: newStatus,
    timeline: [
      ...prev.timeline,
      {
        status: newStatus,
        description,
        date: new Date().toISOString()
      }
    ]
  }));

  // 🔥 STEP 2: clear input
  setDescription("");

  try {
    // 🔥 STEP 3: API call (backend update)
    await UpdateOrderStatus(orderDetails?.sourceOrderId || id, {status:newStatus, description:description});

    // 🔥 STEP 4: refresh latest data (sync with DB)
    await FetchData();

  } catch (error) {
    console.error("Error updating order:", error);
  }
};

  if (loading) return <Container>Loading...</Container>;
  if (!order) return <Container>Order not found.</Container>;
  const trackerStatus = order.status === "out_for_delivery" ? "shipped" : order.status;
  const currentIndex = statusSteps.indexOf(trackerStatus);
  const completedStatuses = order.timeline.map((t) => t.status);
  return (
    <Container>
      <Header>
        <Title>Order Status #{order.displayOrderId?.slice(-8)?.toUpperCase()}</Title>
        {/* <AdminToggle onClick={() => setIsAdmin(!isAdmin)}>
          {isAdmin ? "👁 View as User" : "🛠 View as Admin"}
        </AdminToggle> */}
      </Header>

      {/* 🧾 Order Summary */}
      <Card>
        <Subtitle>Status Tracker</Subtitle>
        <StatusWrapper>
          {statusSteps.map((step, index) => {
            const isCancelled = order.status === "cancelled";
            const isActive = !isCancelled && index <= currentIndex;
            const isCancelStep = isCancelled && step === "cancelled";
            return (
              <StatusStep key={step}>
                <StepCircle active={isActive || isCancelStep}>
                  {index + 1}
                </StepCircle>
                <StepLabel active={isActive || isCancelStep}>
                  {step.toUpperCase() === 'SHIPPED' ? 'In Transit' : step.toUpperCase() }
                </StepLabel>
                {index < statusSteps.length - 1 && (
                  <StepLine
                    active={currentIndex > index && order.status !== "cancelled"}
                    cancelled={isCancelled}
                  />
                )}
              </StatusStep>
            );
          })}
        </StatusWrapper>

        {/* 🧑‍💼 Admin Form */}
        {isAdmin && (
          <Form onSubmit={handleSubmit}>
            <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {statusSteps.map((s) => {
                const isDisabled = completedStatuses.includes(s);

                return (
                  <option key={s} value={s} disabled={isDisabled}>
                    {s} {isDisabled ? "✓" : ""}
                  </option>
                );
              })}
            </Select>

            <Input
              type="text"
              placeholder="Enter step description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <SubmitButton type="submit">Submit Update</SubmitButton>
          </Form>
        )}
      </Card>

      {/* 🏠 Delivery Address */}
      <Card>
        <Subtitle>Delivery Address</Subtitle>
        <AddressBox>
          <p><strong>Receivers Name - {order.address.name}</strong></p>
          <p><strong>Receivers 📞 - {order.address.phone}</strong></p>
          <p>Street - {order.address.street}</p>
          <p>
            {order.address.city}, {order.address.state} - {order.address.zip}
          </p>
        </AddressBox>
      </Card>

      {/* 🕒 Timeline */}
      <Card>
        <Subtitle>Order Timeline</Subtitle>
        <TimelineWrapper>
          {order.timeline.map((event, index) => (
            <TimelineItem key={index}>
              <TimelineDot cancelled={event.status === "cancelled"} />
              <TimelineContent>
                <strong
                  style={{
                    color: event.status === "cancelled" ? "#dc2626" : "#111827",
                  }}
                >
                  {event.status.toUpperCase()}
                </strong>
                <p>{event.description}</p>
                <TimeText>{new Date(event.date).toLocaleString()}</TimeText>
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineWrapper>
      </Card>
    </Container>
  );
};

export default OrderStatusPage;

/* 🎨 Styled Components */

const Container = styled.div`
  margin: 0px auto;
  padding: 20px;
  font-family: "Inter", sans-serif;
  color: #222;

  @media (max-width: 640px) {
    padding: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AdminToggle = styled.button`
  background: #22c55e;
  border: none;
  color: white;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #16a34a;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    font-size: 22px;
    overflow-wrap: anywhere;
  }
`;

const Subtitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  margin-bottom: 20px;

  @media (max-width: 480px) {
    padding: 14px;
    border-radius: 10px;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const Label = styled.p`
  color: #6b7280;
  font-size: 14px;
`;

const Value = styled.p`
  font-size: 16px;
  font-weight: 500;
`;

const SmallText = styled.p`
  font-size: 13px;
  color: #9ca3af;
`;

const StatusWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  margin-top: 10px;

  @media (max-width: 640px) {
    justify-content: flex-start;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 10px;
  }
`;

const StatusStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;

  @media (max-width: 640px) {
    min-width: 82px;
    flex: 0 0 82px;
  }
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${({ active }) => (active ? "#22c55e" : "#d1d5db")};
  background: ${({ active }) => (active ? "#22c55e" : "#fff")};
  color: ${({ active }) => (active ? "#fff" : "#9ca3af")};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: 0.3s;
`;

const StepLabel = styled.p`
  margin-top: 8px;
  font-size: 14px;
  color: ${({ active }) => (active ? "#22c55e" : "#9ca3af")};
  font-weight: ${({ active }) => (active ? "600" : "400")};
  text-align: center;
  overflow-wrap: anywhere;
`;

const StepLine = styled.div`
  position: absolute;
  top: 15px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: ${({ active }) => (active ? "#22c55e" : "#e5e7eb")};
  z-index: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
`;

const Select = styled.select`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 15px;
  background: #f9fafb;
  cursor: pointer;
  outline: none;
`;

const Input = styled.input`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 15px;
  background: #fff;
  outline: none;
`;

const SubmitButton = styled.button`
  background: #22c55e;
  border: none;
  color: white;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  font-weight: 500;
  &:hover {
    background: #16a34a;
  }
`;

const AddressBox = styled.div`
  background: #f9fafb;
  border-radius: 10px;
  padding: 15px;
  font-size: 15px;
  line-height: 1.6;
`;

const TimelineWrapper = styled.div`
  position: relative;
  margin-top: 10px;
  padding-left: 20px;
  border-left: 2px solid #e5e7eb;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  position: relative;
`;

const TimelineDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ active }) => (active ? "#22c55e" : "#d1d5db")};
  position: absolute;
  left: -7px;
  top: 5px;
`;

const TimelineContent = styled.div`
  margin-left: 15px;
  font-size: 14px;
  color: #374151;
`;

const TimeText = styled.p`
  color: #6b7280;
  font-size: 12px;
  margin-top: 3px;
`;
